import React from "react";
import { AiFillMinusCircle, AiFillPlusCircle } from "react-icons/ai";
import ShortTruncateCopy from "components/common/ShortTruncateCopy";
import moment from "moment";
import { convertToFloat } from "utils/formatNumber";
import StatusRender from "views/admin/payments/components/StatusRender";
import AdvancedInfoModal from "views/admin/payments/components/AdvancedInfoModalNew";
import CustomToolTip from "components/tooltips/new";
import {
  Popover, PopoverTrigger, PopoverContent, PopoverHeader, PopoverArrow,
} from "@chakra-ui/react";
import Card from "components/card";
import { getImage } from "utils/commonFunction";
import DivLoader from "components/divloader/DivLoader";
import { FiCheckCircle, FiEye, FiUpload } from "react-icons/fi";
import { 
    getKycDocumentData, 
    getHistoryData,
    getCustomerTransactions,
    getCustomerPaymentMethods,
    getCustomerKyc,
} from "api/graphQL_customers";
import Searchbox from "components/fields/Searchbox";
import Pagination from "components/pagination";

const ExpandedCustomer = React.memo(({ row,
    roleData,
    paymentData,
    activeGatewayTransactions,
    activePaymentMethod,
    openGatewayTransactions,
    openPaymentMethods,
    getGatewayName,
    getCardSVG,
    getCardType,
    uploadingKYC,
    handleFileChange,
    fetchClients,
    fetchedCustomerIds,
    setActiveGatewayTransactions,
    setActivePaymentMethod
}:any)=>{

    const initialFetchDone = React.useRef(false);

    const [transactions, setTransactions] = React.useState<any[]>([]);
    const [paymentMethods, setPaymentMethods] = React.useState<any[]>([]);
    const [kyc, setKyc] = React.useState<any[]>([]);

    const [transactionPage, setTransactionPage] = React.useState(1);
    const [paymentMethodPage, setPaymentMethodPage] = React.useState(1);
    const [kycPage, setKycPage] = React.useState(1);

    const [transactionTotalPages, setTransactionTotalPages] = React.useState(0);
    const [paymentMethodTotalPages, setPaymentMethodTotalPages] = React.useState(0);
    const [kycTotalPages, setKycTotalPages] = React.useState(0);

    const [transactionTotal, setTransactionTotal] = React.useState(0);
    const [paymentMethodTotal, setPaymentMethodTotal] = React.useState(0);
    const [kycTotal, setKycTotal] = React.useState(0);

    const [transactionSearch, setTransactionSearch] = React.useState("");
    const [paymentMethodSearch, setPaymentMethodSearch] = React.useState("");
    const [kycSearch, setKycSearch] = React.useState("");

    const [loadingTransactions, setLoadingTransactions] = React.useState(false);
    const [loadingPaymentMethods, setLoadingPaymentMethods] = React.useState(false);
    const [loadingKyc, setLoadingKyc] = React.useState(false);

    const [loadingDocument, setLoadingDocument] = React.useState<string | null>(null);
    const [paymentHistory, setPaymentHistory] = React.useState<Record<string, any[]>>({});
    const [loadingHistory, setLoadingHistory] = React.useState<string | null>(null);

    React.useEffect(() => {
        const customerId = row?.original?.customer_id;

        if (!customerId) return;

        if (fetchedCustomerIds.current.has(customerId)) {
            return;
        }

        fetchedCustomerIds.current.add(customerId);

        fetchTransactions(1, "");
        fetchPaymentMethods(1, "");
        fetchKyc(1, "");
    }, [row?.original?.customer_id]);

    const handleViewDocument = async (kycId: string) => {
        try {
            setLoadingDocument(kycId);
            const res = await getKycDocumentData(kycId);
            if (!res?.status || !res?.data?.document) {
                return;
            }
            const document = res.data.document;
            const [meta, b64] = document.split(",");
            const mime = meta.match(/data:(.*);base64/)?.[1] || "application/pdf";
            const bytes = atob(b64);
            const arr = new Uint8Array(bytes.length);
            for (let i = 0; i < bytes.length; i++) {
                arr[i] = bytes.charCodeAt(i);
            }
            const url = URL.createObjectURL(
                new Blob([arr], { type: mime })
            );
            window.open(url, "_blank");
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingDocument(null);
        }
    };

    const fetchPaymentHistory = async (paymentId: string) => {
        if (paymentHistory[paymentId]) return;
        try {
            setLoadingHistory(paymentId);
            const res = await getHistoryData(paymentId);
            if (res?.status) {
                setPaymentHistory({
                    ...paymentHistory,
                    [paymentId]: res.data.payment_history || [],
                });
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoadingHistory(null);
        }
    };

    const fetchTransactions = async (
        page = transactionPage,
        search = transactionSearch
    ) => {
        try {
            setLoadingTransactions(true);

            const response = await getCustomerTransactions({
            customerId: row.original.customer_id,
            page,
            per_page: 50,
            searchTexts: search,
            });

            if (response?.status === "Error") {
            setTransactions([]);
            setTransactionTotalPages(0);
            setTransactionTotal(0);
            return;
            }

            const transactionData = (response?.[0] || []).map(
                (transaction: any) => {
                    if (typeof transaction === "string") {
                    try {
                        return JSON.parse(transaction);
                    } catch {
                        return null;
                    }
                    }

                    return transaction;
                }
            ).filter(Boolean);

            setTransactions(transactionData);
            setTransactionTotalPages(response?.[1]?.total_pages || 0);
            setTransactionTotal(response?.[1]?.total_items || 0);
        } catch (error) {
            console.error(error);
            setTransactions([]);
        } finally {
            setLoadingTransactions(false);
        }
    };

    const fetchPaymentMethods = async (
        page = paymentMethodPage,
        search = paymentMethodSearch
    ) => {
        try {
            setLoadingPaymentMethods(true);

            const response = await getCustomerPaymentMethods({
            customerId: row.original.customer_id,
            page,
            per_page: 50,
            searchTexts: search,
            });

            if (response?.status === "Error") {
            setPaymentMethods([]);
            setPaymentMethodTotalPages(0);
            setPaymentMethodTotal(0);
            return;
            }

            const paymentMethodData = (response?.[0] || []).map(
                (method: any) => ({
                    ...method,
                    created_at:method.created_at ?? method.createdAt ?? "",
                    updated_at:method.updated_at ?? method.updatedAt ?? "",
                    payment_method_id:method.payment_method_id ?? method.paymentMethodId ?? "",
                    card_number:method.card_number ?? method.cardNumber ?? "",
                    card_type:method.card_type ?? method.cardType ?? "",
                    wallet_address:method.wallet_address ?? method.walletAddress ?? "",
                    bank_name:method.bank_name ?? method.bankName ?? "",
                    bank_branch:method.bank_branch ?? method.bankBranch ?? "",
                    account_number:method.account_number ?? method.accountNumber ?? "",
                    account_type:method.account_type ?? method.accountType ?? "",
                    swift_no:method.swift_no ?? method.swiftNo ?? "",
                })
            );

            setPaymentMethods(paymentMethodData);
            setPaymentMethodTotalPages(response?.[1]?.total_pages || 0);
            setPaymentMethodTotal(response?.[1]?.total_items || 0);
        } catch (error) {
            console.error(error);
            setPaymentMethods([]);
        } finally {
            setLoadingPaymentMethods(false);
        }
    };

    const fetchKyc = async (
        page = kycPage,
        search = kycSearch
    ) => {
        try {
            setLoadingKyc(true);

            const response = await getCustomerKyc({
            customerId: row.original.customer_id,
            page,
            per_page: 50,
            searchTexts: search,
            });

            if (response?.status === "Error") {
            setKyc([]);
            setKycTotalPages(0);
            setKycTotal(0);
            return;
            }

            setKyc(response?.[0] || []);
            setKycTotalPages(response?.[1]?.total_pages || 0);
            setKycTotal(response?.[1]?.total_items || 0);
        } catch (error) {
            console.error(error);
            setKyc([]);
        } finally {
            setLoadingKyc(false);
        }
    };

    const groupedTransactions = React.useMemo(() => {
        return transactions.reduce((acc: any, txn: any) => {
            if (!txn.gateway_id) return acc;

            if (!acc[txn.gateway_id]) {
            acc[txn.gateway_id] = [];
            }

            acc[txn.gateway_id].push(txn);

            return acc;
        }, {});
    }, [transactions]);

    const groupedPaymentMethods = React.useMemo(() => {
        return paymentMethods.reduce((acc: any, method: any) => {
            if (!method.type) return acc;

            if (!acc[method.type]) {
            acc[method.type] = [];
            }

            acc[method.type].push(method);

            return acc;
        }, {});
    }, [paymentMethods]);

    return (

        <>
            <tr className="clt-expanded-row">
                <td colSpan={8} className="clt-expanded-td">
                    <div className="clt-expanded-inner">

                    {/* Source Client */}
                    <div className="clt-section">
                        <div className="clt-section-header">
                        <div className="clt-section-icon clt-section-icon--blue">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                            </svg>
                        </div>
                        <h4 className="clt-section-title">Source Client</h4>
                        </div>
                        <div className="clt-inner-table-wrap">
                        <table className="clt-inner-table">
                            <thead><tr>
                            <th className="clt-inner-th">Client</th>
                            <th className="clt-inner-th">Reference ID</th>
                            </tr></thead>
                            <tbody><tr className="clt-inner-tr">
                            <td className="clt-inner-td">{row.original.source_client}</td>
                            <td className="clt-inner-td">{row.original.reference_id ?? "-"}</td>
                            </tr></tbody>
                        </table>
                        </div>
                    </div>

                    {/* Gateways */}
                    <div className="clt-section">
                        <div className="clt-section-header">
                            <div className="clt-section-icon clt-section-icon--amber">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                                </svg>
                            </div>
                            <h4 className="clt-section-title">Gateways</h4>
                            <div style={{marginLeft: "auto"}}>
                                <Searchbox
                                    onSearch={(value: string) => {
                                        setTransactionSearch(value);
                                        setTransactionPage(1);
                                        fetchTransactions(1, value);
                                        setActiveGatewayTransactions("")
                                    }}
                                />
                            </div>
                        </div>
                        {loadingTransactions ? (
                        <div className="clt-loading">
                            <div className="clt-spinner" />
                            <p className="clt-loading-txt">Loading transactions......</p>
                        </div>
                        ) : (
                            Object.keys(groupedTransactions).length > 0 ? (
                            <>
                                <div className="clt-inner-table-wrap">
                                    <table className="clt-inner-table">
                                    <thead><tr>
                                        <th className="clt-inner-th">Gateway {roleData?.[0]?.customers?.value?.show_customer_gateway_name ? "Name" : "Hash ID"}</th>
                                        <th className="clt-inner-th">Customer ID</th>
                                        <th className="clt-inner-th">Transactions</th>
                                    </tr></thead>
                                    <tbody>
                                        {Object.entries(groupedTransactions).map(([gatewayId, transactions]: [string, any]) => (
                                        <React.Fragment key={gatewayId}>
                                            <tr className="clt-inner-tr">
                                            <td className="clt-inner-td">
                                                <div className="clt-expand-cell clt-expand-cell--sm" onClick={() => openGatewayTransactions(gatewayId + ":" + row.original.customer_id)}>
                                                {activeGatewayTransactions === gatewayId + ":" + row.original.customer_id
                                                    ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
                                                    : <AiFillPlusCircle className="clt-expand-icon" />}
                                                {roleData?.[0]?.customers?.value?.show_customer_gateway_name ? getGatewayName(gatewayId) : gatewayId}
                                                </div>
                                            </td>
                                            <td className="clt-inner-td">-</td>
                                            <td className="clt-inner-td">
                                                <div className="clt-txn-pill clt-txn-pill--sm">{transactions.length}</div>
                                            </td>
                                            </tr>
                                            {activeGatewayTransactions === gatewayId + ":" + row.original.customer_id && (
                                            <tr>
                                                <td colSpan={3} className="clt-inner-td-nested">
                                                <table className="clt-inner-table">
                                                    <thead><tr>
                                                    {["Date","Amount","Transaction ID","Internal ID","Gateway","Descriptor","Card Type","Status",
                                                        ...((roleData[0]?.customers?.value?.view_customer_payment_response_information || roleData[0]?.customers?.value?.view_customer_gateway_response_information) ? ["Action"] : [])
                                                        ].map((h) => (
                                                        <th key={h} className="clt-inner-th">{h}</th>
                                                    ))}
                                                    </tr></thead>
                                                    <tbody>
                                                    {transactions.map((txn: any, idx: any) => (
                                                        <tr key={idx} className="clt-inner-tr">
                                                        <td className="clt-inner-td clt-nowrap">{moment(txn.created_at).format("YYYY-MM-DD HH:mm:ss")}</td>
                                                        <td className="clt-inner-td clt-nowrap clt-upper">
                                                            {convertToFloat(txn.amount)} {txn.currency}
                                                            {txn.converted_amount && txn.converted_currency && <span> ({txn.converted_amount} {txn.converted_currency})</span>}
                                                        </td>
                                                        <td className="clt-inner-td"><ShortTruncateCopy info={txn?.et_id} showCopy={true} /></td>
                                                        <td className="clt-inner-td"><ShortTruncateCopy info={txn?.id} showCopy={true} /></td>
                                                        <td className="clt-inner-td">
                                                            {!roleData[0]?.customers?.value?.show_customer_gateway_name
                                                            ? <ShortTruncateCopy info={txn.gateway_id} showCopy={true} />
                                                            : <span className="clt-gw-name">
                                                                <img style={{ height: "auto", width: "15px" }} src={getImage(getGatewayName(txn.gateway_id))} alt="" />
                                                                <p>{txn.gateway_id ? getGatewayName(txn.gateway_id) : "-"}</p>
                                                                </span>}
                                                        </td>
                                                        <td className="clt-inner-td">{txn?.meta_info?.Descriptor}</td>
                                                        <td className="clt-inner-td">{txn?.meta_info?.binInformation ? getCardSVG(txn?.meta_info?.binInformation?.card_type) : "-"}</td>
                                                        <td className="clt-inner-td">
                                                            <CustomToolTip
                                                            content={
                                                                loadingHistory === txn.id ? (
                                                                    <div className="p-3">Loading...</div>
                                                                ) : paymentHistory[txn.id]?.length ? (
                                                                    paymentHistory[txn.id].map((h: any, i: number) => (
                                                                        <Card className="m-2 shadow" key={i}>
                                                                            <div className="border max-w-[70vh] border-white p-3">
                                                                                {i === 0 ? (
                                                                                    <p>
                                                                                        Created at {h.created_at} by API call from [{h.client} - {h.ip}] with status {h.status}
                                                                                    </p>
                                                                                ) : (
                                                                                    <p>
                                                                                        Status change from {h.previous_status} to {h.status} at {h.status_changed_date}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </Card>
                                                                    ))
                                                                ) : (
                                                                    <span>No history available.</span>
                                                                )
                                                            }
                                                            // trigger={<button className="flex items-center"><StatusRender status={txn?.status} value={txn?.status} /></button>}
                                                            trigger={
                                                                <button
                                                                    className="flex items-center"
                                                                    onMouseEnter={() => fetchPaymentHistory(txn.id)}
                                                                >
                                                                    <StatusRender status={txn?.status} value={txn?.status} />
                                                                </button>
                                                            }
                                                            header="Status History"
                                                            />
                                                        </td>
                                                        {(roleData[0]?.customers?.value?.view_customer_payment_response_information || roleData[0]?.customers?.value?.view_customer_gateway_response_information) && (
                                                            <td className="clt-inner-td">
                                                            <AdvancedInfoModal info={txn} client_is_deleted={false} />
                                                            </td>
                                                        )}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                                </td>
                                            </tr>
                                            )}
                                        </React.Fragment>
                                        ))}
                                    </tbody>
                                    </table>
                                </div>
                                {/* <Pagination
                                    setPage={(newPage: number) => {
                                        setTransactionPage(newPage);
                                        fetchTransactions(newPage, transactionSearch);
                                    }}
                                    page={transactionPage}
                                    totalpage={transactionTotalPages}
                                    currentPage={transactionPage}
                                    pageSize={10}
                                    setPageSize={() => {}}
                                    arraySize={[10]}
                                /> */}
                            </>
                            ) : (
                            <div className="clt-empty-section">No transactions available.</div>
                            )
                        )}
                    </div>

                    {/* Payment Methods */}
                    <div className="clt-section">
                        <div className="clt-section-header">
                            <div className="clt-section-icon clt-section-icon--green">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                                </svg>
                            </div>
                            <h4 className="clt-section-title">Payment Methods</h4>
                            <div style={{marginLeft: "auto"}}>
                                <Searchbox
                                    onSearch={(value: string) => {
                                        setPaymentMethodSearch(value);
                                        setPaymentMethodPage(1);
                                        fetchPaymentMethods(1, value);
                                        setActivePaymentMethod("")
                                    }}
                                />
                            </div>
                        </div>
                        {loadingPaymentMethods ? (
                        <div className="clt-loading">
                            <div className="clt-spinner" />
                            <p className="clt-loading-txt">Loading payment methods......</p>
                        </div>
                        ) : (
                            Object.keys(groupedPaymentMethods).length > 0 ? (
                            <>
                                <div className="clt-inner-table-wrap">
                                    <table className="clt-inner-table">
                                    <thead><tr>
                                        <th className="clt-inner-th">Type</th>
                                        <th className="clt-inner-th">Count</th>
                                    </tr></thead>
                                    <tbody>
                                        {Object.entries(groupedPaymentMethods).map(([methodId, methods]: [string, any]) => (
                                        <React.Fragment key={methodId}>
                                            <tr className="clt-inner-tr">
                                            <td className="clt-inner-td">
                                                <div className="clt-expand-cell clt-expand-cell--sm" onClick={() => openPaymentMethods(methodId + ":" + row.original.customer_id)}>
                                                {activePaymentMethod === methodId + ":" + row.original.customer_id
                                                    ? <AiFillMinusCircle className="clt-expand-icon clt-expand-icon--open" />
                                                    : <AiFillPlusCircle className="clt-expand-icon" />}
                                                <span className="clt-pm-type-badge">{methodId}</span>
                                                </div>
                                            </td>
                                            <td className="clt-inner-td"><div className="clt-txn-pill clt-txn-pill--sm">{methods.length}</div></td>
                                            </tr>
                                            {activePaymentMethod === methodId + ":" + row.original.customer_id && (
                                            <tr>
                                                <td colSpan={3} className="clt-inner-td-nested">
                                                <table className="clt-inner-table">
                                                    <thead><tr>
                                                    <th className="clt-inner-th">Created Date</th>
                                                    <th className="clt-inner-th">Payment Method ID</th>
                                                    {methodId === "card" && <><th className="clt-inner-th">Last 4</th><th className="clt-inner-th">Card Type</th></>}
                                                    {methodId === "bank" && <><th className="clt-inner-th">IBAN</th><th className="clt-inner-th">Swift</th><th className="clt-inner-th">Bank Name</th><th className="clt-inner-th">Account Number</th></>}
                                                    {methodId === "crypto" && <th className="clt-inner-th">Wallet Address</th>}
                                                    </tr></thead>
                                                    <tbody>
                                                    {methods.map((m: any, idx: any) => (
                                                        <tr key={idx} className="clt-inner-tr">
                                                        <td className="clt-inner-td clt-nowrap">{m.created_at ? moment(m.created_at).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
                                                        <td className="clt-inner-td"><ShortTruncateCopy info={m.payment_method_id} showCopy={true} /></td>
                                                        {methodId === "card" && <><td className="clt-inner-td">{m.card_number?.slice(-4)}</td><td className="clt-inner-td">{getCardSVG(getCardType(m.card_number))}</td></>}
                                                        {methodId === "bank" && <><td className="clt-inner-td">{m.iban || "-"}</td><td className="clt-inner-td">{m.swift_no || "-"}</td><td className="clt-inner-td">{m.bank_name || "-"}</td><td className="clt-inner-td">{m.account_number || "-"}</td></>}
                                                        {methodId === "crypto" && <td className="clt-inner-td"><ShortTruncateCopy info={m.wallet_address} showCopy={true} /></td>}
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </table>
                                                </td>
                                            </tr>
                                            )}
                                        </React.Fragment>
                                        ))}
                                    </tbody>
                                    </table>
                                </div>
                                {/* <Pagination
                                    setPage={(newPage: number) => {
                                        setPaymentMethodPage(newPage);
                                        fetchPaymentMethods(newPage, paymentMethodSearch);
                                    }}
                                    page={paymentMethodPage}
                                    totalpage={paymentMethodTotalPages}
                                    currentPage={paymentMethodPage}
                                    pageSize={10}
                                    setPageSize={() => {}}
                                    arraySize={[10]}
                                /> */}
                            </>
                            ) : (
                            <div className="clt-empty-section">No payment methods available.</div>
                            )
                        )}
                    </div>

                    {/* KYC */}
                    <div className="clt-section">
                        <div className="clt-section-header">
                            <div className="clt-section-icon clt-section-icon--purple">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                <polyline points="14 2 14 8 20 8"/>
                                </svg>
                            </div>
                            <h4 className="clt-section-title">KYC</h4>
                            <div style={{marginLeft: "auto"}}>
                                <Searchbox
                                    onSearch={(value: string) => {
                                        setKycSearch(value);
                                        setKycPage(1);
                                        fetchKyc(1, value);
                                    }}
                                />
                            </div>
                        </div>
                        {loadingKyc ? (
                        <div className="clt-loading">
                            <div className="clt-spinner" />
                            <p className="clt-loading-txt">Loading kyc data......</p>
                        </div>
                        ) : (
                            kyc.length > 0 ? (
                            <>
                                <div className="clt-inner-table-wrap">
                                    <table className="clt-inner-table">
                                    <thead><tr>
                                        <th className="clt-inner-th">Created Date</th>
                                        <th className="clt-inner-th">KYC ID</th>
                                        <th className="clt-inner-th">ID Number</th>
                                        <th className="clt-inner-th">ID Type</th>
                                        <th className="clt-inner-th">Issued At</th>
                                        <th className="clt-inner-th">Expired At</th>
                                        <th className="clt-inner-th">Status</th>
                                        {(roleData?.[0]?.customers?.value?.upload_kyc_document || roleData?.[0]?.customers?.value?.view_kyc_document) && (
                                        <th className="clt-inner-th">Actions</th>
                                        )}
                                    </tr></thead>
                                    <tbody>
                                        {kyc.map((kyc_data: any, index: number) => (
                                        <tr key={kyc_data.kycId || index}>
                                            <td className="clt-inner-td clt-nowrap">{kyc_data.createdDate ? moment(kyc_data.createdDate).format("YYYY-MM-DD HH:mm:ss") : "-"}</td>
                                            <td className="clt-inner-td"><ShortTruncateCopy info={kyc_data.kycId} showCopy={true} /></td>
                                            <td className="clt-inner-td clt-upper">{kyc_data.identificationNumber }</td>
                                            <td className="clt-inner-td clt-upper">{kyc_data.identificationType }</td>
                                            <td className="clt-inner-td">{kyc_data.documentIssuedAt  || "-"}</td>
                                            <td className="clt-inner-td">{kyc_data.documentExpiredAt  || "-"}</td>
                                            <td className="clt-inner-td">
                                            {kyc_data.status === "verified"
                                                ? <span className="clt-kyc-status clt-kyc-status--verified">Verified</span>
                                                : <span className="clt-kyc-status clt-kyc-status--unverified">Unverified</span>}
                                            </td>
                                            {(roleData?.[0]?.customers?.value?.upload_kyc_document || roleData?.[0]?.customers?.value?.view_kyc_document) && (
                                            <td className="clt-inner-td">
                                                <div className="clt-kyc-actions">
                                                {roleData?.[0]?.customers?.value?.upload_kyc_document && (
                                                    !kyc_data.hasDocument ? (
                                                    uploadingKYC === kyc_data.kycId ? (
                                                        <div className="clt-kyc-spinner" />
                                                    ) : (
                                                        <Popover trigger="hover" placement="bottom">
                                                        <PopoverTrigger>
                                                            <label htmlFor={`file-${kyc_data.kycId}`} className="cursor-pointer">
                                                            <input type="file" accept="image/*,application/pdf" id={`file-${kyc_data.kycId}`} style={{ display: "none" }}
                                                                onChange={e => { e.currentTarget.blur(); handleFileChange(e, kyc_data.kycId, row.original.customer_id); }} />
                                                            <div className="clt-kyc-action-btn clt-kyc-action-btn--upload"><FiUpload size={14} /></div>
                                                            </label>
                                                        </PopoverTrigger>
                                                        <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                                            <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">Upload Document</PopoverHeader>
                                                            <PopoverArrow bg="#eff6ff" />
                                                        </PopoverContent>
                                                        </Popover>
                                                    )
                                                    ) : (
                                                    <Popover trigger="hover" placement="bottom">
                                                        <PopoverTrigger>
                                                        <div className="clt-kyc-action-btn clt-kyc-action-btn--done"><FiCheckCircle size={14} /></div>
                                                        </PopoverTrigger>
                                                        <PopoverContent border="1px solid #bbf7d0" borderRadius="12px" bg="#f0fdf4" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                                        <PopoverHeader bg="#dcfce7" fontSize="12px" fontWeight="700" color="#15803d" borderBottom="1px solid #bbf7d0" px="12px" py="9px">Document Uploaded</PopoverHeader>
                                                        <PopoverArrow bg="#f0fdf4" />
                                                        </PopoverContent>
                                                    </Popover>
                                                    )
                                                )}
                                                {roleData?.[0]?.customers?.value?.view_kyc_document && kyc_data.status === "verified" && kyc_data.hasDocument  && (
                                                    <Popover trigger="hover" placement="bottom">
                                                    <PopoverTrigger>
                                                        <button
                                                            className="clt-kyc-action-btn clt-kyc-action-btn--view"
                                                            onClick={() => handleViewDocument(kyc_data.kycId)}
                                                            disabled={loadingDocument === kyc_data.kycId}
                                                        >
                                                            {loadingDocument === kyc_data.kycId ? (
                                                                <div className="clt-kyc-spinner" />
                                                            ) : (
                                                                <FiEye size={14} />
                                                            )}
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent border="1px solid #bfdbfe" borderRadius="12px" bg="#eff6ff" maxW="180px" p="0" overflow="hidden" zIndex="20">
                                                        <PopoverHeader bg="#dbeafe" fontSize="12px" fontWeight="700" color="#1e40af" borderBottom="1px solid #bfdbfe" px="12px" py="9px">View Document</PopoverHeader>
                                                        <PopoverArrow bg="#eff6ff" />
                                                    </PopoverContent>
                                                    </Popover>
                                                )}
                                                </div>
                                            </td>
                                            )}
                                        </tr>
                                        ))}
                                    </tbody>
                                    </table>
                                </div>
                                {/* <Pagination
                                    setPage={(newPage: number) => {
                                        setKycPage(newPage);
                                        fetchKyc(newPage, kycSearch);
                                    }}
                                    page={kycPage}
                                    totalpage={kycTotalPages}
                                    currentPage={kycPage}
                                    pageSize={10}
                                    setPageSize={() => {}}
                                    arraySize={[10]}
                                /> */}
                            </>
                            ) : (
                            <div className="clt-empty-section">No KYC data available.</div>
                            )
                        )}
                    </div>
                    </div>
                </td>
            </tr>
        </>

    )

});

export default ExpandedCustomer;
